import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { getProducts } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { ArrowUpRight } from "lucide-react";
import logoMark from "@/assets/logo-mark-color.png";

export default function Home() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, 80]);

  const headline = t("home.headline");
  const chapters = t("home.chapters");

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="home-page" className="croc-bg text-foreground">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-32 md:pt-28">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_80%_30%,rgba(255,85,0,0.10),transparent_45%),radial-gradient(circle_at_15%_75%,rgba(10,37,64,0.08),transparent_45%)]" />

        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left copy */}
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="font-mono-x text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-6 font-bold"
            >
              {t("home.heroTag")}
            </motion.p>

            <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-tighter text-[hsl(var(--primary))] break-words">
              {headline.map((line, i) => (
                <span key={line} className="block overflow-hidden pr-1">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ delay: 0.3 + i * 0.12, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="block text-[clamp(1.9rem,5.4vw,4.6rem)]"
                  >
                    {i === 1 ? <span className="text-[hsl(var(--accent))]">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
            >
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-[hsl(var(--accent))] text-white font-mono-x text-sm uppercase tracking-widest px-8 py-4 transition-all duration-300 hover:glow-orange"
              >
                {t("home.heroCta")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <p className="font-body text-slate-500 max-w-xs leading-relaxed">
                {t("home.heroSub")}
              </p>
            </motion.div>
          </div>

          {/* Right product showcase */}
          <motion.div
            style={{ y: heroY }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-3xl border border-slate-200 shadow-xl overflow-hidden h-[460px] bg-[hsl(var(--primary))] grid-lines flex flex-col items-center justify-center text-center px-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
              <p className="relative font-mono-x text-xs uppercase tracking-[0.4em] text-white/50 mb-4">SlimeGlass</p>
              <h3 className="relative font-display font-extrabold uppercase tracking-tighter text-white leading-[0.82] text-[clamp(3.5rem,9vw,6rem)]">
                DROP<br /><span className="text-[hsl(var(--accent))]">02</span>
              </h3>
              <div className="relative mt-6 flex gap-2">
                <span className="font-mono-x text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] bg-white rounded-full px-3 py-1.5">12MM</span>
                <span className="font-mono-x text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] bg-white rounded-full px-3 py-1.5">9 HOLE</span>
                <span className="font-mono-x text-[10px] uppercase tracking-widest text-white/80 border border-white/30 rounded-full px-3 py-1.5">BLUE / ORANGE</span>
              </div>
            </div>
            <motion.img
              src={logoMark}
              alt="SlimeGlass"
              animate={{ y: [-8, 8, -8], rotate: 360 }}
              transition={{
                y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
                rotate: { duration: 16, repeat: Infinity, ease: "linear" },
              }}
              className="hidden lg:block absolute -right-6 -top-6 z-10 w-24 drop-shadow-[0_0_30px_rgba(255,85,0,0.35)]"
            />
          </motion.div>
        </div>
      </section>

      <Marquee text={t("home.marquee1")} variant="solid" />

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-8 py-24 md:py-36" data-testid="manifesto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-extrabold text-4xl sm:text-6xl uppercase tracking-tighter mb-20 max-w-3xl text-[hsl(var(--primary))]"
        >
          {t("home.manifesto1")} <span className="text-stroke-orange">{t("home.manifesto2")}</span>
        </motion.h2>

        <div className="space-y-px">
          {chapters.map((c, i) => (
            <motion.div
              key={c.t}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 border-t border-slate-200 items-start"
            >
              <div className="md:col-span-3">
                <span className="font-mono-x text-5xl md:text-6xl text-slate-200 font-bold">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="md:col-span-4 font-display font-bold text-2xl md:text-3xl tracking-tight text-slate-900">{c.t}</h3>
              <p className="md:col-span-5 font-body text-slate-500 text-lg leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* THE DROP */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-8 pb-24 md:pb-36" data-testid="drop-grid">
        <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
          <div>
            <p className="font-mono-x text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-3 font-bold">{t("home.availableNow")}</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-6xl uppercase tracking-tighter text-[hsl(var(--primary))]">{t("home.theDrop")}</h2>
          </div>
          <Link to="/shop" className="font-mono-x text-xs uppercase tracking-widest text-slate-500 hover:text-[hsl(var(--accent))] transition-colors inline-flex items-center gap-2">
            {t("home.viewAll")} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {products.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      <Marquee text={t("home.marquee2a")} text2={t("home.marquee2b")} variant="outline" speed="fast" />

      {/* CTA */}
      <section className="relative overflow-hidden py-28 md:py-40 bg-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,85,0,0.16),transparent_60%)]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-extrabold text-5xl sm:text-7xl md:text-8xl uppercase tracking-tighter leading-[0.9] text-white"
          >
            {t("home.ctaTitle1")}<br /><span className="text-[hsl(var(--accent))]">{t("home.ctaTitle2")}</span>
          </motion.h2>
          <Link
            to="/shop"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-[hsl(var(--accent))] text-white font-mono-x text-sm uppercase tracking-widest px-10 py-5 transition-all duration-300 hover:glow-orange"
          >
            {t("home.enterShop")} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
